import React, { useState } from 'react';
import { Calculator, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalculadoraModal = ({ tipo, isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  
  // Estado específico para cada tipo de calculadora
  const [formData, setFormData] = useState({
    // Campos comuns para contato
    nome: '',
    telefone: '',
    email: '',
    // Campos para Rescisão
    salario_bruto: '',
    data_admissao: '',
    data_saida: '',
    motivo_saida: '',
    // Campos para Férias
    salario_ferias: '',
    dias_ferias: '',
    ferias_vencidas: false,
    // Campos para 13º Salário
    salario_decimo: '',
    meses_trabalhados_decimo: '',
    // Campos para Horas Extras
    salario_hora: '',
    horas_extras_50: '',
    horas_extras_100: '',
    dias_semana: ''
  });

  const configs = {
    rescisao: {
      titulo: 'Calcular Rescisão Trabalhista',
      descricao: 'Análise das verbas rescisórias conforme legislação trabalhista',
      mensagemWhatsApp: 'Olá, Dr. Leandro! Usei a ferramenta de cálculo de rescisão trabalhista do site e gostaria de analisar meu caso com mais detalhes.'
    },
    ferias: {
      titulo: 'Calcular Férias',
      descricao: 'Análise de férias proporcionais, vencidas e abono',
      mensagemWhatsApp: 'Olá, Dr. Leandro! Usei a ferramenta de cálculo de férias do site e gostaria de verificar se estou recebendo corretamente.'
    },
    decimo: {
      titulo: 'Calcular 13º Salário',
      descricao: 'Cálculo do 13º salário proporcional aos meses trabalhados',
      mensagemWhatsApp: 'Olá, Dr. Leandro! Usei a ferramenta de cálculo de 13º salário do site e gostaria de confirmar se meu empregador está pagando corretamente.'
    },
    horas: {
      titulo: 'Calcular Horas Extras',
      descricao: 'Cálculo de horas extras com adicionais de 50% e 100%',
      mensagemWhatsApp: 'Olá, Dr. Leandro! Usei a ferramenta de cálculo de horas extras do site e gostaria de verificar se tenho valores a receber.'
    }
  };

  const config = configs[tipo] || configs.rescisao;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateStep1 = () => {
    switch(tipo) {
      case 'rescisao':
        if (!formData.salario_bruto || !formData.data_admissao || !formData.data_saida || !formData.motivo_saida) {
          toast.error('Por favor, preencha todos os campos');
          return false;
        }
        break;
      case 'ferias':
        if (!formData.salario_ferias || !formData.dias_ferias) {
          toast.error('Por favor, preencha todos os campos');
          return false;
        }
        break;
      case 'decimo':
        if (!formData.salario_decimo || !formData.meses_trabalhados_decimo) {
          toast.error('Por favor, preencha todos os campos');
          return false;
        }
        break;
      case 'horas':
        if (!formData.salario_hora || (!formData.horas_extras_50 && !formData.horas_extras_100)) {
          toast.error('Por favor, preencha o salário e ao menos um tipo de hora extra');
          return false;
        }
        break;
      default:
        return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const calcularLocal = () => {
    let resultado = {};
    
    switch(tipo) {
      case 'rescisao':
        // Calcular rescisão via backend
        return null; // Vai usar o backend
      
      case 'ferias':
        const salarioFerias = parseFloat(formData.salario_ferias) || 0;
        const diasFerias = parseInt(formData.dias_ferias) || 30;
        const feriasBase = (salarioFerias / 30) * diasFerias;
        const tercoConstitucional = feriasBase / 3;
        const totalFerias = feriasBase + tercoConstitucional;
        
        resultado = {
          ferias_base: feriasBase,
          terco_constitucional: tercoConstitucional,
          total_estimado: totalFerias,
          detalhamento: {
            dias_ferias: diasFerias,
            ferias_vencidas: formData.ferias_vencidas
          }
        };
        break;
      
      case 'decimo':
        const salarioDecimo = parseFloat(formData.salario_decimo) || 0;
        const mesesDecimo = parseInt(formData.meses_trabalhados_decimo) || 0;
        const decimoCalculado = (salarioDecimo / 12) * Math.min(mesesDecimo, 12);
        
        resultado = {
          decimo_terceiro: decimoCalculado,
          total_estimado: decimoCalculado,
          detalhamento: {
            meses_trabalhados: Math.min(mesesDecimo, 12),
            salario_base: salarioDecimo
          }
        };
        break;
      
      case 'horas':
        const salarioHora = parseFloat(formData.salario_hora) || 0;
        const valorHora = salarioHora / 220; // Jornada mensal de 220 horas
        const horas50 = parseInt(formData.horas_extras_50) || 0;
        const horas100 = parseInt(formData.horas_extras_100) || 0;
        
        const valorHoras50 = horas50 * (valorHora * 1.5);
        const valorHoras100 = horas100 * (valorHora * 2);
        const totalHoras = valorHoras50 + valorHoras100;
        
        resultado = {
          valor_hora_normal: valorHora,
          horas_50_valor: valorHoras50,
          horas_100_valor: valorHoras100,
          total_estimado: totalHoras,
          detalhamento: {
            quantidade_horas_50: horas50,
            quantidade_horas_100: horas100,
            valor_hora_50: valorHora * 1.5,
            valor_hora_100: valorHora * 2
          }
        };
        break;
      
      default:
        return null;
    }
    
    return resultado;
  };

  const salvarLead = async () => {
    try {
      await axios.post(`${API}/leads`, {
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        mensagem: `Lead capturado via Calculadora de ${config.titulo}. Valor estimado: R$ ${resultado?.total_estimado?.toFixed(2) || 'N/A'}`,
        origem: `calculadora_${tipo}`
      });
    } catch (error) {
      console.error('Erro ao salvar lead:', error);
    }
  };

  const handleCalcular = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let resultadoCalculo;
      
      if (tipo === 'rescisao') {
        // Usar o backend para rescisão (cálculo mais complexo)
        const response = await axios.post(`${API}/calculadora`, {
          salario_bruto: parseFloat(formData.salario_bruto),
          data_admissao: formData.data_admissao,
          data_saida: formData.data_saida,
          motivo_saida: formData.motivo_saida,
          nome: formData.nome,
          telefone: formData.telefone,
          email: formData.email,
          origem: tipo
        });
        resultadoCalculo = response.data;
      } else {
        // Cálculo local para os outros tipos
        resultadoCalculo = calcularLocal();
        // Salvar lead manualmente
        await salvarLead();
      }
      
      setResultado(resultadoCalculo);
      toast.success('Cálculo realizado com sucesso!');
      setStep(3);
    } catch (error) {
      toast.error('Erro ao calcular. Tente novamente.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setFormData({
      nome: '',
      telefone: '',
      email: '',
      salario_bruto: '',
      data_admissao: '',
      data_saida: '',
      motivo_saida: '',
      salario_ferias: '',
      dias_ferias: '',
      ferias_vencidas: false,
      salario_decimo: '',
      meses_trabalhados_decimo: '',
      salario_hora: '',
      horas_extras_50: '',
      horas_extras_100: '',
      dias_semana: ''
    });
    setResultado(null);
    onClose();
  };

  // Renderizar campos específicos por tipo
  const renderCamposEspecificos = () => {
    switch(tipo) {
      case 'rescisao':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-oxford font-semibold mb-2">Salário Bruto (R$) *</label>
              <Input
                type="number"
                name="salario_bruto"
                value={formData.salario_bruto}
                onChange={handleInputChange}
                placeholder="Ex: 3500.00"
                className="input-premium"
                data-testid="salario-bruto-input"
              />
            </div>

            <div>
              <label className="block text-oxford font-semibold mb-2">Motivo da Saída *</label>
              <Select value={formData.motivo_saida} onValueChange={(value) => handleSelectChange('motivo_saida', value)}>
                <SelectTrigger className="input-premium" data-testid="motivo-select">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sem_justa_causa">Demissão sem Justa Causa</SelectItem>
                  <SelectItem value="pedido_demissao">Pedido de Demissão</SelectItem>
                  <SelectItem value="justa_causa">Justa Causa</SelectItem>
                  <SelectItem value="acordo">Acordo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-oxford font-semibold mb-2">Data de Admissão *</label>
              <Input
                type="date"
                name="data_admissao"
                value={formData.data_admissao}
                onChange={handleInputChange}
                className="input-premium"
                data-testid="data-admissao-input"
              />
            </div>

            <div>
              <label className="block text-oxford font-semibold mb-2">Data de Saída *</label>
              <Input
                type="date"
                name="data_saida"
                value={formData.data_saida}
                onChange={handleInputChange}
                className="input-premium"
                data-testid="data-saida-input"
              />
            </div>
          </div>
        );

      case 'ferias':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-oxford font-semibold mb-2">Salário Bruto (R$) *</label>
              <Input
                type="number"
                name="salario_ferias"
                value={formData.salario_ferias}
                onChange={handleInputChange}
                placeholder="Ex: 3500.00"
                className="input-premium"
                data-testid="salario-ferias-input"
              />
            </div>

            <div>
              <label className="block text-oxford font-semibold mb-2">Dias de Férias *</label>
              <Select value={formData.dias_ferias} onValueChange={(value) => handleSelectChange('dias_ferias', value)}>
                <SelectTrigger className="input-premium" data-testid="dias-ferias-select">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 dias (período completo)</SelectItem>
                  <SelectItem value="20">20 dias + 10 abono</SelectItem>
                  <SelectItem value="15">15 dias</SelectItem>
                  <SelectItem value="10">10 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="ferias_vencidas"
                  checked={formData.ferias_vencidas}
                  onChange={handleInputChange}
                  className="w-5 h-5 rounded border-gray-300 text-champagne focus:ring-champagne"
                  data-testid="ferias-vencidas-checkbox"
                />
                <span className="text-oxford font-medium">Tenho férias vencidas (mais de 12 meses sem tirar)</span>
              </label>
              {formData.ferias_vencidas && (
                <p className="text-sm text-red-600 mt-2 ml-8">
                  * Férias vencidas devem ser pagas em dobro conforme a CLT
                </p>
              )}
            </div>
          </div>
        );

      case 'decimo':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-oxford font-semibold mb-2">Salário Bruto (R$) *</label>
              <Input
                type="number"
                name="salario_decimo"
                value={formData.salario_decimo}
                onChange={handleInputChange}
                placeholder="Ex: 3500.00"
                className="input-premium"
                data-testid="salario-decimo-input"
              />
            </div>

            <div>
              <label className="block text-oxford font-semibold mb-2">Meses Trabalhados no Ano *</label>
              <Select value={formData.meses_trabalhados_decimo} onValueChange={(value) => handleSelectChange('meses_trabalhados_decimo', value)}>
                <SelectTrigger className="input-premium" data-testid="meses-decimo-select">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(mes => (
                    <SelectItem key={mes} value={String(mes)}>{mes} {mes === 1 ? 'mês' : 'meses'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2 bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Como funciona:</strong> O 13º salário é calculado proporcionalmente aos meses trabalhados no ano. 
                Cada mês trabalhado equivale a 1/12 do salário bruto.
              </p>
            </div>
          </div>
        );

      case 'horas':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-oxford font-semibold mb-2">Salário Mensal Bruto (R$) *</label>
              <Input
                type="number"
                name="salario_hora"
                value={formData.salario_hora}
                onChange={handleInputChange}
                placeholder="Ex: 3500.00"
                className="input-premium"
                data-testid="salario-hora-input"
              />
              <p className="text-sm text-gray-500 mt-1">Base de cálculo: 220 horas mensais</p>
            </div>

            <div>
              <label className="block text-oxford font-semibold mb-2">Horas Extras 50% (quantidade)</label>
              <Input
                type="number"
                name="horas_extras_50"
                value={formData.horas_extras_50}
                onChange={handleInputChange}
                placeholder="Ex: 20"
                className="input-premium"
                data-testid="horas-50-input"
              />
              <p className="text-sm text-gray-500 mt-1">Dias úteis (segunda a sábado)</p>
            </div>

            <div>
              <label className="block text-oxford font-semibold mb-2">Horas Extras 100% (quantidade)</label>
              <Input
                type="number"
                name="horas_extras_100"
                value={formData.horas_extras_100}
                onChange={handleInputChange}
                placeholder="Ex: 8"
                className="input-premium"
                data-testid="horas-100-input"
              />
              <p className="text-sm text-gray-500 mt-1">Domingos e feriados</p>
            </div>

            <div className="md:col-span-2 bg-amber-50 p-4 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Importante:</strong> Informe a quantidade total de horas extras que você trabalhou 
                e não recebeu (ou recebeu parcialmente). Ex: se trabalhou 2h extra por dia durante 10 dias = 20 horas.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Renderizar resultado específico por tipo
  const renderResultado = () => {
    if (!resultado) return null;

    switch(tipo) {
      case 'rescisao':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-l-4 border-champagne">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">Saldo de Salário</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.saldo_salario?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-champagne">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">Férias + 1/3</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.ferias_proporcionais?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-champagne">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">13º Proporcional</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.decimo_terceiro?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-champagne">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">Multa 40% FGTS</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.multa_fgts?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>
          </div>
        );

      case 'ferias':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-l-4 border-champagne">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">Férias ({resultado.detalhamento?.dias_ferias} dias)</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.ferias_base?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-champagne">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">1/3 Constitucional</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.terco_constitucional?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>

            {resultado.detalhamento?.ferias_vencidas && (
              <div className="md:col-span-2 bg-red-50 p-4 rounded-lg">
                <p className="text-red-800 font-semibold">
                  ⚠️ Você indicou ter férias vencidas. O valor acima pode ser dobrado conforme a CLT Art. 137.
                </p>
              </div>
            )}
          </div>
        );

      case 'decimo':
        return (
          <div className="grid grid-cols-1 gap-4">
            <Card className="border-l-4 border-champagne">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">13º Salário Proporcional ({resultado.detalhamento?.meses_trabalhados} meses)</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.decimo_terceiro?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                Cálculo: R$ {resultado.detalhamento?.salario_base?.toLocaleString('pt-BR', {minimumFractionDigits: 2})} ÷ 12 × {resultado.detalhamento?.meses_trabalhados} meses
              </p>
            </div>
          </div>
        );

      case 'horas':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-l-4 border-champagne">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">Valor da Hora Normal</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.valor_hora_normal?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-champagne">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">Horas 50% ({resultado.detalhamento?.quantidade_horas_50}h × R$ {resultado.detalhamento?.valor_hora_50?.toFixed(2)})</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.horas_50_valor?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-champagne md:col-span-2">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-1">Horas 100% ({resultado.detalhamento?.quantidade_horas_100}h × R$ {resultado.detalhamento?.valor_hora_100?.toFixed(2)})</p>
                <p className="text-xl font-bold text-oxford">R$ {resultado.horas_100_valor?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-serif text-2xl text-oxford flex items-center gap-3">
              <Calculator className="w-7 h-7 text-champagne" />
              {config.titulo}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetAndClose}
              className="rounded-full"
              data-testid="close-modal-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-gray-600 mt-2">{config.descricao}</p>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-6 py-4">
            {renderCamposEspecificos()}

            <Button 
              onClick={handleNext}
              className="btn-champagne w-full py-6 text-lg font-semibold"
              data-testid="continuar-btn"
            >
              Continuar
            </Button>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleCalcular} className="space-y-6 py-4">
            <div className="bg-champagne/10 border border-champagne/30 rounded-lg p-4 mb-6">
              <p className="text-oxford font-medium text-center">
                Para ver o resultado, informe seus dados de contato
              </p>
            </div>

            <div>
              <label className="block text-oxford font-semibold mb-2">Nome Completo *</label>
              <Input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                required
                placeholder="Seu nome completo"
                className="input-premium"
                data-testid="nome-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-oxford font-semibold mb-2">WhatsApp *</label>
                <Input
                  type="tel"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleInputChange}
                  required
                  placeholder="(62) 9 9999-9999"
                  className="input-premium"
                  data-testid="telefone-input"
                />
              </div>

              <div>
                <label className="block text-oxford font-semibold mb-2">E-mail *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="seu@email.com"
                  className="input-premium"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button 
                type="button"
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 py-6 border-2 border-oxford text-oxford hover:bg-oxford hover:text-white"
                data-testid="voltar-btn"
              >
                Voltar
              </Button>
              <Button 
                type="submit"
                disabled={loading}
                className="btn-champagne flex-1 py-6 font-semibold"
                data-testid="calcular-btn"
              >
                {loading ? 'Calculando...' : 'Ver Resultado'}
              </Button>
            </div>
          </form>
        )}

        {step === 3 && resultado && (
          <div className="space-y-6 py-4">
            <div className="bg-gradient-oxford p-8 rounded-2xl text-white text-center">
              <p className="text-champagne font-semibold mb-2 text-lg">Valor Total Estimado</p>
              <p className="font-serif text-5xl font-bold">
                R$ {resultado.total_estimado?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
              </p>
            </div>

            {renderResultado()}

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                <strong>Aviso Legal:</strong> Estimativa baseada na legislação vigente. Análise individual depende de avaliação documental.
              </p>
            </div>

            <Button 
              onClick={() => window.open(`https://wa.me/5562996951404?text=${encodeURIComponent(`${config.mensagemWhatsApp} Fiz a análise prévia e o resultado foi R$ ${resultado.total_estimado?.toFixed(2)}.`)}`, '_blank')}
              className="btn-champagne w-full py-6 text-lg font-semibold"
              data-testid="whatsapp-resultado-btn"
            >
              Falar com Dr. Leandro no WhatsApp
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CalculadoraModal;
